import React from 'react';
import { SVGAssets } from '@business/assets/vectors';
import { ContextMenu } from '@business/workflows/ComponentUI/ContextMenu';
import { Checkbox } from '../Checkbox';
import { Button } from '../Button';

interface PhotoGridProps {
  photos?: any[];
  selectedPhotos?: string[];
  onPhotoSelect?: (photoId: string) => void;
  onToggleFavorite?: (
    photoId: string,
    currentStatus: string | undefined,
    e?: React.MouseEvent
  ) => void;
  onRowClick: (photo: any) => void;
  contextMenuItems: (photo: any, accessType?: string) => any[];
  isFromTrashPage?: boolean;
  accessType?: string;
  hideFavorite?: boolean;
  hideContextMenu?: boolean;
  hideCheckbox?: boolean;
}

export const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  selectedPhotos,
  onPhotoSelect,
  onToggleFavorite,
  onRowClick,
  contextMenuItems,
  isFromTrashPage,
  accessType,
  hideFavorite,
  hideContextMenu,
  hideCheckbox
}) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
      {photos.map((photo: any, index: number) => (
        <div
          key={photo.id || index}
          className={`relative group ${isFromTrashPage ? '' : 'cursor-pointer'} rounded-lg overflow-visible bg-white border border-gray-200 hover:shadow-lg transition-shadow}`}
          onClick={() => {
            if (!isFromTrashPage) {
              onRowClick(photo);
            }
          }}>
          <div className="aspect-square overflow-hidden rounded-lg">
            {photo?.type === 'document' ? (
              <div
                onClick={e => {
                  e.stopPropagation();
                  onRowClick(photo);
                }}
                className="w-full h-full flex flex-col items-center justify-center bg-gray-300 hover:bg-gray-200 transition-colors">
                <SVGAssets.DocIcon className="w-12 h-12 text-gray-400" />
              </div>
            ) : photo?.type === 'audio' ? (
              <div
                onClick={e => {
                  e.stopPropagation();
                  onRowClick(photo);
                }}
                className="w-full h-full flex items-center justify-center bg-gray-300 hover:bg-gray-200 transition-colors">
                <SVGAssets.Audio className="w-12 h-12 text-gray-400" />
              </div>
            ) : (
              <>
                {/* 🖼 IMAGE / VIDEO THUMB */}
                <img
                  src={
                    photo?.thumbNailUrl ||
                    photo?.asset?.thumbNailUrl ||
                    '/placeholder-image.jpg'
                  }
                  alt={photo?.fileName || `Photo ${index + 1}`}
                  className="w-full h-full object-cover"
                />

                {/* Play icon ONLY for video */}
                {photo?.type?.startsWith('video/') && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-16 h-16 bg-black/60 rounded-full flex items-center justify-center">
                      <SVGAssets.PlayButton className="w-8 h-8 text-white ml-1" />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="absolute top-2 left-2 z-[5] flex items-center gap-2">
            {!hideCheckbox && (
              <Checkbox
                name={`${photo.id}`}
                checked={(selectedPhotos || []).includes(photo.id)}
                onChange={e => {
                  e.stopPropagation();
                  onPhotoSelect?.(photo.id);
                }}
                onClick={e => e.stopPropagation()}
              />
            )}
            {!isFromTrashPage && !hideFavorite && (
              <Button
                btnStyle="link"
                onClick={e =>
                  onToggleFavorite(photo.id, photo?.favorite?.status, e)
                }
                className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center hover:bg-gray-100 hover-star ${
                  photo?.favorite?.status === 'Active'
                    ? 'hover-star-active'
                    : ''
                }`}>
                <SVGAssets.FavoriteIcons className="w-5 h-5" />
              </Button>
            )}
          </div>

          <div className="absolute bottom-2 right-2 z-[5] flex items-center gap-2">
            <div className="flex items-center gap-1  rounded-full px-2 py-1">
              <SVGAssets.CommentIcons className="w-4 h-4 text-white" />
              <span className="text-white text-xs font-medium">
                {photo?.comments?.length || 0}
              </span>
            </div>
            <div className="flex items-center gap-1  rounded-full px-2 py-1">
              {photo?.isLiked > 0 ? (
                <SVGAssets.LikeRed className="w-4 h-4" />
              ) : (
                <SVGAssets.LikeIcons className="w-4 h-4 text-white" />
              )}
              <span className="text-white text-xs font-medium">
                {photo?.likes?.length || 0}
              </span>
            </div>
          </div>

          {!hideContextMenu && (
            <div className="absolute top-2 right-2 transition-opacity z-10">
              <div className="relative">
                <ContextMenu
                  menuItems={contextMenuItems(photo, accessType)}
                  icon={
                    <div className="w-8 h-8 group-hover:shadow-md group-hover:bg-white group-hover:text-black text-white rounded-full  flex items-center justify-center hover:bg-gray-100">
                      <SVGAssets.ThreeDots className="w-4 h-4" />
                    </div>
                  }
                  itemContainerClass="!min-w-[180px]"
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
